import { axiosInstance } from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "@/lib/queryClient";
import { toast } from "sonner";

// Standalone functions for direct use (e.g. in bulk operations)
export async function createStudentFn(data: any) {
  return (await axiosInstance.post("/api/students", data)).data;
}

export async function updateStudentFn({
  studentNumber,
  data,
}: {
  studentNumber: string;
  data: any;
}) {
  return (await axiosInstance.patch(`/api/students/${studentNumber}`, data))
    .data;
}

export async function deleteStudentFn(studentNumber: string) {
  return (await axiosInstance.delete(`/api/students/${studentNumber}`)).data;
}

export async function addAcademicRecordFn(studentNumber: string, data: any) {
  return (
    await axiosInstance.post(`/api/students/${studentNumber}/academic`, data)
  ).data;
}

export async function addHealthRecordFn(studentNumber: string, data: any) {
  return (
    await axiosInstance.post(`/api/students/${studentNumber}/health`, data)
  ).data;
}

export async function addEconomicRecordFn(studentNumber: string, data: any) {
  return (
    await axiosInstance.post(`/api/students/${studentNumber}/economic`, data)
  ).data;
}

export async function addPsychosocialRecordFn(
  studentNumber: string,
  data: any
) {
  return (
    await axiosInstance.post(
      `/api/students/${studentNumber}/psychosocial`,
      data
    )
  ).data;
}

const Student = {
  Create: {
    useMutation: (options = {}) => {
      return useMutation({
        mutationFn: createStudentFn,
        onSuccess: (req, variables) => {
          toast.success("Student created successfully!");
          queryClient.invalidateQueries({ queryKey: ["Students"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to create student";
          toast.error(message);
        },
        ...options,
      });
    },
  },

  Update: {
    useMutation: (options = {}) => {
      return useMutation({
        mutationFn: updateStudentFn,
        onSuccess: (req, variables) => {
          toast.success("Student updated successfully!");
          queryClient.invalidateQueries({ queryKey: ["Students"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to update student";
          toast.error(message);
        },
        ...options,
      });
    },
  },

  Delete: {
    useMutation: (options = {}) => {
      return useMutation({
        mutationFn: deleteStudentFn,
        onSuccess: (req, variables) => {
          toast.success("Student deleted successfully!");
          queryClient.invalidateQueries({ queryKey: ["Students"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to delete student";
          toast.error(message);
        },
        ...options,
      });
    },
  },

  // Academic Records
  Academic: {
    Create: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: ({ studentNumber, data }: { studentNumber: string; data: any }) => 
            addAcademicRecordFn(studentNumber, data),
          onSuccess: (res, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Academic"],
            });
            toast.success("Academic record added successfully!");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to add academic record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
    Update: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: async ({ id, studentNumber, data }: { id: number; studentNumber: string; data: any }) => {
            return (
              await axiosInstance.patch(
                `/api/students/${studentNumber}/academic?id=${id}`,
                data
              )
            ).data;
          },
          onSuccess: (req, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Academic"],
            });
            toast.success("Academic record updated successfully!");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to update academic record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
    Delete: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: async ({ id, studentNumber }: { id: number; studentNumber: string }) => {
            return (
              await axiosInstance.delete(
                `/api/students/${studentNumber}/academic?id=${id}`
              )
            ).data;
          },
          onSuccess: (req, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Academic"],
            });
            toast.success("Academic record deleted");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to delete academic record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
  },

  // Health Records
  Health: {
    Create: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: ({ studentNumber, data }: { studentNumber: string; data: any }) => 
            addHealthRecordFn(studentNumber, data),
          onSuccess: (res, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Health"],
            });
            toast.success("Health record added successfully!");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to add health record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
    Update: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: async ({ id, studentNumber, data }: { id: number; studentNumber: string; data: any }) => {
            return (
              await axiosInstance.patch(
                `/api/students/${studentNumber}/health?id=${id}`,
                data
              )
            ).data;
          },
          onSuccess: (res, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Health"],
            });
            toast.success("Health record updated successfully!");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to update health record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
    Delete: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: async ({id , studentNumber }: { id: number; studentNumber: string }) => {
            return (
              await axiosInstance.delete(
                `/api/students/${studentNumber}/health?id=${id}`
              )
            ).data;
          },
          onSuccess: (req, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Health"],
            });
            toast.success("Health record deleted");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to delete health record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
  },

  // Economic Records
  Economic: {
    Create: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: ({ studentNumber, data }: { studentNumber: string; data: any }) => 
            addEconomicRecordFn(studentNumber, data),
          onSuccess: (res, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Economic"],
            });
            toast.success("Economic record added successfully!");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to add economic record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
    Update: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: async ({ id, studentNumber, data }: { id: number; studentNumber: string; data: any }) => {
            return (
              await axiosInstance.patch(
                `/api/students/${studentNumber}/economic?id=${id}`,
                data
              )
            ).data;
          },
          onSuccess: (res, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Economic"],
            });
            toast.success("Economic record updated successfully!");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to update economic record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
    Delete: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: async ({ id, studentNumber }: { id: number , studentNumber: string }) => {
            return (
              await axiosInstance.delete(
                `/api/students/${studentNumber}/economic?id=${id}`
              )
            ).data;
          },
          onSuccess: (res, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Economic"],
            });
            toast.success("Economic record deleted");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to delete economic record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
  },

  // Psychosocial Records
  Psychosocial: {
    Create: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: ({ studentNumber, data }: { studentNumber: string; data: any }) =>
            addPsychosocialRecordFn(studentNumber, data),
          onSuccess: (req, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Psychosocial"],
            });
            toast.success("Psychosocial record added successfully!");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to add psychosocial record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
    Update: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: async ({ id, studentNumber, data }: { id: number; studentNumber: string; data: any }) => {
            return (
              await axiosInstance.patch(
                `/api/students/${studentNumber}/psychosocial?id=${id}`,
                data
              )
            ).data;
          },
          onSuccess: (req, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Psychosocial"],
            });
            toast.success("Psychosocial record updated successfully!");
          },
          onError: (error: any) => {
            const message =
              error?.response?.data?.error || "Failed to update psychosocial record";
            toast.error(message);
          },
          ...options,
        });
      },
    },
    Delete: {
      useMutation: (options = {}) => {
        return useMutation({
          mutationFn: async ({ id, studentNumber }: { id: number; studentNumber: string }) => {
            return (
              await axiosInstance.delete(
                `/api/students/${studentNumber}/psychosocial?id=${id}`
              )
            ).data;
          },
          onSuccess: (req, variables) => {
            queryClient.invalidateQueries({
              queryKey: ["Student", variables.studentNumber, "Psychosocial"],
            });
            toast.success("Psychosocial record deleted");
          },
          ...options,
        });
      },
    },
  },

  Import: {
    useMutation: (options = {}) => {
      return useMutation({
        mutationFn: async (formData: FormData) => {
          return (
            await axiosInstance.post("/api/students/import", formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            })
          ).data;
        },
        onSuccess: (req, variables) => {
          queryClient.invalidateQueries({ queryKey: ["Students"] });
        },
        ...options,
      });
    },
  },
};

export default Student;
